import { Component } from '@theme/component';

/**
 * Homepage hero video (sections/hero-video.liquid).
 *
 * - Starts the video once the page has loaded, so it never competes with the poster (the LCP
 *   image) for bandwidth, and fades it in over the poster when it is playing.
 * - Pauses the video while the hero is off screen, and never plays it for visitors who prefer
 *   reduced motion (they get the poster).
 *
 * The headline animation is pure CSS, switched on by an inline script before first paint.
 *
 * @typedef {{ video?: HTMLVideoElement }} HeroVideoRefs
 * @extends {Component<HeroVideoRefs>}
 */
class HeroVideoComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    const { video } = this.refs;
    if (!video) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }

    // The mobile cut is chosen by `<source media>` in the markup, not swapped in here: a swap
    // after autoplay had started meant phones downloaded both files (19 Oct 2026).
    video.muted = true;
    video.addEventListener('playing', () => video.setAttribute('data-playing', ''), { once: true });

    // Nothing downloads until the page has loaded (the markup has no `autoplay` and
    // `preload="none"`). The video is several MB and was competing with the poster, the LCP
    // image, for a phone's bandwidth: 13.9 s LCP on a simulated mobile connection (19 Oct 2026).
    const start = () => {
      this.#observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
      this.#observer.observe(this);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('hero-video-component')) {
  customElements.define('hero-video-component', HeroVideoComponent);
}
